import { Injectable } from '@angular/core';
import { NetworkingService } from '../networking.service';
import { API } from '../../data/api';
import { Lesson } from '../../data/models/Lesson';
import { catchError, finalize, map, Observable, of, throwError } from 'rxjs';
import { SharedDataService } from '../../helper/services/shared-data.service';
import { environment } from '../../../environments/environment';
import { LessonPageItemTypeEnum } from '../../data/enums/LessonPageItemType';

@Injectable({
  providedIn: 'root'
})
export class LessonService {
  constructor(private networkingService: NetworkingService, private sharedDataService: SharedDataService) { }

  getLessonList(courseId: string): Observable<Lesson[]> {
    const endpoint = API.lessonList(courseId)
    return this.networkingService.getRequest<Lesson[]>(endpoint).pipe(
      map((lessons: Lesson[]) => {
        return lessons.map((lesson) => ({
          ...lesson,
          imageData: `${environment.url}/${environment.backendVersion}/${API.lessonPictureDownload(lesson.lesson_picture_id)}`,
        }));
      }),
      catchError((error) => {
        return of([]);
      }),
      finalize(() => {
        this.sharedDataService.toggleProgressBarOff();
      })
    );
  }

  getLessonListPublished(courseId: string): Observable<Lesson[]> {
    const endpoint = API.lessonListPublished(courseId)
    return this.networkingService.getRequest<Lesson[]>(endpoint).pipe(
      map((lessons: Lesson[]) => {
        return lessons.map((lesson) => ({
          ...lesson,
          imageData: `${environment.url}/${environment.backendVersion}/${API.lessonPictureDownload(lesson.lesson_picture_id)}`,
        }));
      }),
      catchError((error) => {
        return of([]);
      }),
      finalize(() => {
        this.sharedDataService.toggleProgressBarOff();
      })
    );
  }

  getLessonDetails(id: string): Observable<Lesson> {
    const endpoint = API.lessonDetails(id);
  
    return this.networkingService.getRequest<Lesson>(endpoint).pipe(
      map((lesson: Lesson) => {
        lesson.imageData = `${environment.url}/${environment.backendVersion}/${API.lessonPictureDownload(lesson.lesson_picture_id)}`;
  
        lesson.lesson_pages?.forEach(page => {
          page.items?.forEach(item => {
            if (item.type?.title === LessonPageItemTypeEnum.VIDEO) {
              item.videoData = `${environment.url}/${environment.backendVersion}/${API.lessonPageItemDownload(item.id)}`;
            }
  
            if (item.title === 'None') {
              item.title = null;
            }
          });
        });
  
        return lesson;
      }),
      finalize(() => {
        this.sharedDataService.toggleProgressBarOff();
      })
    );
  }

  createLesson(lessonData: {
    title: string;
    prerequisiteIds?: string[]; 
    lessonPictureId: string;
    courseId: string;
  }): Observable<Lesson> {
  
    const payload: any = {
      title: lessonData.title,
      lesson_picture_id: lessonData.lessonPictureId,
      course_id: lessonData.courseId,
      ...(lessonData.prerequisiteIds && lessonData.prerequisiteIds.length > 0
        ? { prerequisite_ids: lessonData.prerequisiteIds }
        : {}),
    };
  
    return this.networkingService.postRequest(API.lessonCreate, payload).pipe(
      map((response) => ({
        ...response,
        imageData: `${environment.url}/${environment.backendVersion}/${API.lessonPictureDownload(response.lesson_picture_id)}`,
      })),
      catchError((error) => {
        console.error('Failed to create lesson:', error);
        return throwError(() => new Error('Failed to create lesson'));
      }),
      finalize(() => {
        this.sharedDataService.toggleProgressBarOff();
      })
    );
  }

  alterLesson(
    lessonId: string,
    lessonData: {
      title?: string;
      prerequisiteIds?: string[];
      lessonPictureId?: string;
    }
  ): Observable<Lesson> {
    const endpoint = API.lessonAlter(lessonId);
  
    const payload: any = {
      ...(lessonData.title !== undefined ? { title: lessonData.title } : {}),
      ...(lessonData.prerequisiteIds !== undefined ? { prerequisite_ids: lessonData.prerequisiteIds } : {}),
      ...(lessonData.lessonPictureId !== undefined ? { lesson_picture_id: lessonData.lessonPictureId } : {}),
    };
  
    return this.networkingService.putRequest(endpoint, payload).pipe(
      map((response) => ({
        ...response,
        imageData: `${environment.url}/${environment.backendVersion}/${API.lessonPictureDownload(response.lesson_picture_id)}`,
      })),
      catchError((error) => {
        console.error('Failed to alter lesson:', error);
        return throwError(() => new Error('Failed to alter lesson'));
      }),
      finalize(() => {
        this.sharedDataService.toggleProgressBarOff();
      })
    );
  }

  deleteLesson(lessonId: string): Observable<any> {
    const endpoint = API.lessonDelete(lessonId);
  
    return this.networkingService.deleteRequest(endpoint).pipe(
      catchError((error) => {
        console.error('Failed to delete lesson:', error);
        return throwError(() => new Error('Failed to delete lesson'));
      }),
      finalize(() => {
        this.sharedDataService.toggleProgressBarOff();
      })
    );
  }

  toggleLessonPublication(lessonId: string): Observable<Lesson> {
    const endpoint = API.lessonTogglePublication(lessonId);
  
    return this.networkingService.patchRequest(endpoint, {}).pipe(
      map((lesson: Lesson) => ({
        ...lesson,
        imageData: `${environment.url}/${environment.backendVersion}/${API.lessonPictureDownload(lesson.lesson_picture_id)}`,
      })),
      catchError((error) => {
        console.error('Failed to toggle lesson publication:', error);
        return throwError(() => new Error('Failed to toggle lesson publication'));
      }),
      finalize(() => {
        this.sharedDataService.toggleProgressBarOff();
      })
    );
  }
}
